import { ChangeEvent, useState } from 'react'
import { faCalendar, faCalendarDays } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import {
  buildCountdownBreakdown,
  buildCountdownHint,
  formatCountdownLabel,
  formatMillisForDateInput,
  parseDateInputToMillis,
} from './helpers'
import {
  Card,
  Glyph,
  NumberBlock,
  NumberValue,
  NumberHint,
  SetterRow,
  SetterInput,
  SetterButton,
  PromptCard,
  PromptText,
  PromptTitle,
  PromptHint,
} from './styled'
import { MoveInCountdownProps } from './types'

/**
 * Dashboard widget that surfaces the countdown to the renter's
 * preferredMoveInDate. When the date isn't set yet, it nudges the
 * renter to add one inline. Updating the date persists to the profile.
 *
 * @param {MoveInCountdownProps} props
 * @returns {JSX.Element | null}
 */
const MoveInCountdown = ({
  shouldShow = true,
}: MoveInCountdownProps): JSX.Element | null => {
  const { profile, patchProfile } = useQuickPassProfile()
  const [draftDateInputValue, setDraftDateInputValue] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  if (shouldShow === false) {
    return null
  }

  if (profile === null) {
    return null
  }

  const targetMillis = profile.unitPreferences.preferredMoveInDate

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDraftDateInputValue(event.target.value)
    const parsed = parseDateInputToMillis(event.target.value)
    if (parsed === null) {
      return
    }

    setIsSaving(true)
    void patchProfile({
      unitPreferences: {
        ...profile.unitPreferences,
        preferredMoveInDate: parsed,
      },
    }).finally(() => setIsSaving(false))
  }

  const handleClear = (): void => {
    setDraftDateInputValue('')
    void patchProfile({
      unitPreferences: {
        ...profile.unitPreferences,
        preferredMoveInDate: null,
      },
    })
  }

  if (targetMillis === null) {
    return (
      <PromptCard>
        <Glyph aria-hidden="true">
          <Icon icon={faCalendar} />
        </Glyph>
        <PromptText>
          <PromptTitle>When are you trying to move in?</PromptTitle>
          <PromptHint>
            Set a target date and we&apos;ll count down. Helps prioritise the
            short list.
          </PromptHint>
        </PromptText>
        <SetterRow>
          <SetterInput
            type="date"
            value={draftDateInputValue}
            onChange={handleDateChange}
            aria-label="Move-in target date"
          />
        </SetterRow>
      </PromptCard>
    )
  }

  const breakdown = buildCountdownBreakdown(targetMillis)
  const label = formatCountdownLabel(breakdown)
  const hint = buildCountdownHint(breakdown, targetMillis)
  const inputValue = formatMillisForDateInput(targetMillis)

  return (
    <Card>
      <Glyph aria-hidden="true">
        <Icon icon={faCalendarDays} />
      </Glyph>
      <NumberBlock>
        <NumberValue>{label}</NumberValue>
        <NumberHint>{hint}</NumberHint>
      </NumberBlock>
      <SetterRow>
        <SetterInput
          type="date"
          value={inputValue}
          onChange={handleDateChange}
          disabled={isSaving === true}
          aria-label="Move-in target date"
        />
        <SetterButton type="button" onClick={handleClear}>
          Clear
        </SetterButton>
      </SetterRow>
    </Card>
  )
}

export default MoveInCountdown
